import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  FileEdit,
  CheckCircle2,
  XCircle,
  UserPlus,
  LifeBuoy,
  MessageSquare,
  ListChecks,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

// Same visual language as the bell dropdown, extended with EMI reminders.
const TYPE_META = {
  change_request: { icon: FileEdit, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  request_approved: { icon: CheckCircle2, ring: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  request_rejected: { icon: XCircle, ring: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300" },
  connection: { icon: UserPlus, ring: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  support_reply: { icon: LifeBuoy, ring: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" },
  message: { icon: MessageSquare, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  task: { icon: ListChecks, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  task_done: { icon: CheckCircle2, ring: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  emi_due: { icon: CalendarClock, ring: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
};

const PAGE_SIZE = 15;

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

// Friendly labels for the type dropdown; unknown types fall back to a
// title-cased version of the raw type.
const TYPE_LABELS = {
  change_request: "Change requests",
  request_approved: "Approvals",
  request_rejected: "Rejections",
  connection: "Connections",
  support_reply: "Support replies",
  message: "Messages",
  task: "Tasks",
  task_done: "Completed tasks",
  emi_due: "EMI reminders",
  system: "System",
};

const typeLabel = (t) =>
  TYPE_LABELS[t] ||
  String(t || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
        status: filter,
      });
      if (typeFilter !== "all") params.set("type", typeFilter);
      const { data } = await api.get(`/notifications?${params.toString()}`);
      setItems(data.notifications || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
      setAvailableTypes(data.available_types || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, filter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (n) => {
    if (!n.is_read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        window.dispatchEvent(new Event("notif:refresh"));
      } catch {
        /* ignore */
      }
    }
  };

  const onItem = async (n) => {
    await markRead(n);
    if (n.link) navigate(n.link);
    else load();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      window.dispatchEvent(new Event("notif:refresh"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;

  const setFilterAndReset = (f) => {
    setFilter(f);
    setPage(1);
  };

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-600/10 text-emerald-600">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              Activity
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              Notifications
            </h1>
          </div>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition"
          >
            <CheckCheck className="h-4 w-4" /> Mark all read
          </button>
        )}
      </div>

      {/* Filters: read/unread/all tabs + type dropdown */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterAndReset(f.key)}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium transition ${
                filter === f.key
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              {f.label}
              {f.key === "unread" && unread > 0 && (
                <span className="ml-1.5 text-xs opacity-80">({unread})</span>
              )}
            </button>
          ))}
        </div>

        {availableTypes.length > 0 && (
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 text-sm px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              <option value="all">All types</option>
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {typeLabel(t)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="card-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-20 text-center">
            <Bell className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
            <p className="mt-3 text-sm text-slate-400">
              {typeFilter !== "all"
                ? `No ${typeLabel(typeFilter).toLowerCase()} to show.`
                : filter === "unread"
                  ? "No unread notifications."
                  : filter === "read"
                    ? "No read notifications yet."
                    : "No notifications yet."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((n) => {
              const meta = TYPE_META[n.type] || {
                icon: Bell,
                ring: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
              };
              const Icon = meta.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => onItem(n)}
                  className={`w-full flex gap-3 px-4 sm:px-5 py-4 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800/60 ${
                    n.is_read ? "" : "bg-emerald-50/40 dark:bg-emerald-500/[0.06]"
                  }`}
                >
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${meta.ring}`}>
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="block text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {n.title}
                      </span>
                      {!n.is_read && (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                      )}
                    </span>
                    {n.body && (
                      <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                        {n.body}
                      </span>
                    )}
                    <span className="mt-1 block text-[11px] text-slate-400">
                      {timeAgo(n.created_at)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-800 px-4 py-3">
            <span className="text-xs text-slate-500">
              Showing{" "}
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {start + 1}–{Math.min(start + PAGE_SIZE, total)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {total}
              </span>
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                {safePage} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
